/* De lijst van Contacten (/crm/contacts): ophalen, tabs, zoeken, sorteren en de filter
 * in de URL. De tabs volgen de organisatie van het contact: bij een organisatie met een
 * project, uit Charging, leverancier, of zonder organisatie.
 *
 * Relatieve imports (geen @/), zodat de toetsen dit bestand zonder bundel lezen. */

import { lijstGeheugen, vindt } from './geheugen';
import { contactNaam, sorteerNaam } from './labels';
import type { RelatieClient } from './organisaties';

export interface ContactOrganisatie {
  id: string;
  naam: string;
  classificatie: string | null;
  icoon: string | null;
  metProject: boolean;
  charging: boolean;
  gearchiveerd: boolean;
}

export interface LijstContact {
  id: string;
  naam: string;
  /** Sorteersleutel: achternaam zonder tussenvoegsel, dan voornaam. */
  achternaam: string;
  functie: string | null;
  email: string | null;
  telefoon: string | null;
  mobiel: string | null;
  avg: boolean;
  org_id: string | null;
  organisatie: ContactOrganisatie | null;
  aangemaakt: string;
  bijgewerkt: string | null;
  /** De rij zoals hij in de database staat, voor ContactModal. */
  bron: Record<string, any>;
}

export async function haalContacten(client: RelatieClient): Promise<LijstContact[]> {
  const contacten = await client.from('contacts').select('*').is('deleted_at', null);
  if (contacten.error) throw contacten.error;
  const orgIds = [...new Set((contacten.data ?? []).map((c: any) => c.org_id).filter(Boolean))];
  const orgs = new Map<string, ContactOrganisatie>();
  if (orgIds.length) {
    /* Ook een gearchiveerde organisatie: dan staat dat erbij, in plaats van "zonder organisatie". */
    const [o, p, k, l] = await Promise.all([
      client.from('organizations').select('id, name, classification, favicon_url, logo_url, deleted_at').in('id', orgIds),
      client.from('projects').select('id, org_id').is('deleted_at', null).in('org_id', orgIds),
      client.from('charging_opportunities').select('id, org_id').is('deleted_at', null).in('org_id', orgIds),
      client.from('charging_locations').select('id, org_id').is('deleted_at', null).in('org_id', orgIds),
    ]);
    for (const r of [o, p, k, l]) if (r.error) throw r.error;
    const metProject = new Set((p.data ?? []).map((r: any) => r.org_id));
    const charging = new Set([...(k.data ?? []), ...(l.data ?? [])].map((r: any) => r.org_id));
    for (const r of o.data ?? []) {
      orgs.set(r.id, {
        id: r.id,
        naam: r.name?.trim() || '(zonder naam)',
        classificatie: r.classification ?? null,
        icoon: r.favicon_url || r.logo_url || null,
        metProject: metProject.has(r.id),
        charging: charging.has(r.id),
        gearchiveerd: Boolean(r.deleted_at),
      });
    }
  }
  return (contacten.data ?? []).map((c: any) => ({
    id: String(c.id),
    naam: contactNaam(c.first_name, c.last_name),
    achternaam: sorteerNaam(c.last_name, c.first_name),
    functie: c.title?.trim() || null,
    email: c.email?.trim() || null,
    telefoon: c.phone?.trim() || c.mobile?.trim() || null,
    mobiel: c.mobile?.trim() || null,
    avg: Boolean(c.consent_gdpr),
    org_id: c.org_id ?? null,
    organisatie: c.org_id ? orgs.get(c.org_id) ?? null : null,
    aangemaakt: c.created_at,
    bijgewerkt: c.updated_at ?? null,
    bron: c,
  }));
}

/* ── tabs ─────────────────────────────────────────────────────────── */

export const TABS = ['alle', 'projecten', 'charging', 'leveranciers', 'zonder'] as const;
export type Tab = (typeof TABS)[number];

export const TAB_LABEL: Record<Tab, string> = {
  alle: 'Alle',
  projecten: 'Met project',
  charging: 'Charging',
  leveranciers: 'Leveranciers',
  zonder: 'Zonder organisatie',
};

export function inTab(c: LijstContact, tab: Tab): boolean {
  const o = c.organisatie;
  switch (tab) {
    case 'alle': return true;
    case 'projecten': return Boolean(o?.metProject);
    case 'charging': return Boolean(o?.charging);
    case 'leveranciers': return o?.classificatie === 'leverancier';
    case 'zonder': return !o || o.gearchiveerd;
  }
}

/* ── zoeken en sorteren ───────────────────────────────────────────── */

export type Sortering = 'naam' | 'organisatie' | 'bijgewerkt';
export const SORTERINGEN: Sortering[] = ['naam', 'organisatie', 'bijgewerkt'];

export interface LijstFilter {
  zoek: string;
  tab: Tab;
  sortering: Sortering;
  oplopend: boolean;
}

export const STANDAARD_FILTER: LijstFilter = { zoek: '', tab: 'alle', sortering: 'naam', oplopend: true };

export function passendBijZoek(c: LijstContact, zoek: string): boolean {
  return vindt(zoek, [c.naam, c.functie, c.email, c.telefoon, c.mobiel, c.organisatie?.naam]);
}

const tekst = (a: string, b: string) => a.localeCompare(b, 'nl', { sensitivity: 'base' });

export function filterEnSorteer(lijst: LijstContact[], f: LijstFilter): LijstContact[] {
  const uit = lijst.filter((c) => inTab(c, f.tab) && passendBijZoek(c, f.zoek));
  const r = f.oplopend ? 1 : -1;
  const opNaam = (a: LijstContact, b: LijstContact) => tekst(a.achternaam, b.achternaam) || tekst(a.naam, b.naam);
  return uit.sort((a, b) => {
    switch (f.sortering) {
      case 'organisatie': {
        /* zonder organisatie achteraan, in beide richtingen */
        const x = a.organisatie?.naam;
        const y = b.organisatie?.naam;
        if (!x !== !y) return x ? -1 : 1;
        return (x && y ? r * tekst(x, y) : 0) || opNaam(a, b);
      }
      case 'bijgewerkt': {
        const x = a.bijgewerkt ?? a.aangemaakt;
        const y = b.bijgewerkt ?? b.aangemaakt;
        return (x === y ? 0 : x < y ? -r : r) || opNaam(a, b);
      }
      default: return r * opNaam(a, b);
    }
  });
}

export interface TabTelling {
  tab: Tab;
  aantal: number;
}

export function tellingen(lijst: LijstContact[], zoek: string): TabTelling[] {
  const basis = lijst.filter((c) => passendBijZoek(c, zoek));
  return TABS.map((tab) => ({ tab, aantal: basis.filter((c) => inTab(c, tab)).length }));
}

export const beginRichting = (s: Sortering) => s !== 'bijgewerkt';

/* ── filter in de URL ─────────────────────────────────────────────── */

export function filterUitUrl(p: URLSearchParams): LijstFilter {
  const tab = p.get('tab');
  const sort = p.get('sort') as Sortering;
  const sortering = SORTERINGEN.includes(sort) ? sort : STANDAARD_FILTER.sortering;
  const dir = p.get('dir');
  return {
    zoek: p.get('q') ?? '',
    tab: TABS.includes(tab as Tab) ? (tab as Tab) : STANDAARD_FILTER.tab,
    sortering,
    oplopend: dir === 'asc' ? true : dir === 'desc' ? false : beginRichting(sortering),
  };
}

export function filterNaarUrl(f: LijstFilter): Record<string, string> {
  const uit: Record<string, string> = {};
  if (f.zoek) uit.q = f.zoek;
  if (f.tab !== STANDAARD_FILTER.tab) uit.tab = f.tab;
  if (f.sortering !== STANDAARD_FILTER.sortering) uit.sort = f.sortering;
  if (f.oplopend !== beginRichting(f.sortering)) uit.dir = f.oplopend ? 'asc' : 'desc';
  return uit;
}

export const contactLijst = lijstGeheugen('contacten', '/crm/contacts');
